/**
 * useTranslate — Lightweight translation hook backed by lazy-loaded locale JSON files.
 *
 * Returns { t, loading } where t(key) returns the translated string for the
 * active language, falling back to the key itself (English) when the translation
 * is missing or still loading.
 *
 * English translations are always available synchronously (no loading state).
 * Other languages are loaded on demand and cached in memory.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { loadLocale, flattenTranslations } from '../locales';
import enTranslations from '../locales/en.json';

const flatEn = flattenTranslations(enTranslations as Record<string, unknown>);

export function useTranslate(): { t: (key: string) => string; loading: boolean } {
    const { activeLanguage } = useLanguage();
    const [translations, setTranslations] = useState<Record<string, string>>(flatEn);
    const [loading, setLoading] = useState(false);
    const langRef = useRef(activeLanguage);

    useEffect(() => {
        langRef.current = activeLanguage;

        if (activeLanguage === 'en') {
            setTranslations(flatEn);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        loadLocale(activeLanguage).then(loaded => {
            if (!cancelled && langRef.current === activeLanguage) {
                setTranslations(loaded);
                setLoading(false);
            }
        });

        return () => { cancelled = true; };
    }, [activeLanguage]);

    const t = useCallback(
        (key: string): string => translations[key] ?? key,
        [translations]
    );

    return { t, loading };
}
